import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: "/tours", label: t("nav.explore_tours") },
    { path: "/guide", label: t("nav.travel_guide") },
    { path: "/blog", label: t("nav.blog") },
    { path: "/assistant", label: t("nav.assistant"), icon: true },
    { path: "/about", label: t("nav.about") },
  ];

  return (
    <>
      <nav
        data-testid="main-navbar"
        className={`fixed top-2 sm:top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 w-[calc(100%-1rem)] sm:w-auto max-w-[98vw] sm:max-w-[95vw] ${
          scrolled
            ? "glass shadow-lg rounded-2xl sm:rounded-full px-3 sm:px-6 py-2 sm:py-3"
            : "bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-full px-3 sm:px-6 py-2 sm:py-4"
        }`}
      >
        <div className="flex items-center justify-between gap-2 sm:gap-8">
          {/* Brand Wordmark */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center flex-shrink-0 group"
          >
            <span className="font-heading text-lg sm:text-2xl font-black tracking-tight leading-none">
              <span className="text-[#006D77]">Discover</span>{" "}
              <span className="text-[#1D3557]">Cancun</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.slice(1)}`}
                className={`text-sm px-3 py-2 rounded-full transition-colors whitespace-nowrap flex items-center gap-1 ${
                  location.pathname === link.path
                    ? "text-white bg-[#006D77] font-semibold"
                    : "text-[#1D3557] hover:bg-[#006D77]/10"
                }`}
              >
                {link.icon && <MessageCircle className="w-4 h-4" />}
                {link.label}
              </Link>
            ))}
            <LanguageSwitcher />
          </div>

          {/* Mobile: Language + Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
              className="w-10 h-10 rounded-xl bg-[#006D77]/10 flex items-center justify-center text-[#006D77]"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Dropdown Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[76px] left-2 right-2 z-50 bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-[#E2E8F0] p-3 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-white bg-[#006D77]"
                      : "text-[#1D3557] hover:bg-[#006D77]/10"
                  }`}
                >
                  {link.icon && <MessageCircle className="w-5 h-5" />}
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay to close mobile menu */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar;
